import type { ActivityReply } from '@feral/shared/client'
import { Popup } from '@feral/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { type ComponentType, lazy, Suspense, useEffect, useState } from 'react'
import { useChatSocket } from '../features/chat/common.js'
import { openingRoute } from '../lib/onboarding.js'
import type { PinPath } from '../lib/prefs.js'
import { home } from '../lib/prefs.js'
import { onSessionEnd, useSession } from '../lib/session.js'
import { socket } from '../lib/ws.js'
import { PinnedContext, usePinned } from './pin.js'

// "Something went wrong" (bugs.md): downloaded the first time anything does.
const MeetDuties = lazy(() => import('../features/meet/duties.js').then((m) => ({ default: m.MeetDuties })))
const EventMeetDuties = lazy(() =>
  import('../features/events/meetDuty.js').then((m) => ({ default: m.EventMeetDuties })),
)
// Happening now (FR-MAP-17): Trusted members' meets and events, above the tab bar on every screen.
const NowBar = lazy(() => import('../features/now/NowBar.js').then((m) => ({ default: m.NowBar })))
const TroubleNote = lazy(() => import('./TroubleNote.js').then((m) => ({ default: m.TroubleNote })))
/** At most one "Something went wrong" in this long, however much goes wrong at once. */
const TROUBLE_QUIET_MS = 2 * 60_000
// New notifications flash on screen (bugs.md): downloaded with the first one.
const Flashes = lazy(() => import('./Flashes.js').then((m) => ({ default: m.Flashes })))
const MapScreen = lazy(() => import('../features/map/MapScreen.js').then((m) => ({ default: m.MapScreen })))
// Only a removed account ever sees this (WF-TRU-14); nobody else downloads it.
const Appeal = lazy(() => import('../features/safety/Appeal.js').then((m) => ({ default: m.Appeal })))
// Only a member without an email sees this (decision 71), once, until they add one.
const EmailGate = lazy(() =>
  import('../features/settings/EmailGate.js').then((m) => ({ default: m.EmailGate })),
)
// Only a member whose accepted rules are older than the current version sees this (decision 75).
const RulesUpdate = lazy(() => import('../features/entry/rules.js').then((m) => ({ default: m.RulesUpdate })))
// Likewise the Terms and the Privacy Policy, when a new version is published (bugs.md).
const LegalUpdate = lazy(() => import('../features/legal/Legal.js').then((m) => ({ default: m.LegalUpdate })))
/**
 * Where a visit starts (bugs.md): once per visit, an account opening on the map goes where it should
 * be instead (openingRoute): onboarding's step until that's done, then the Gate for a Pending member.
 * Once, so closing it still reaches the map; the next visit, or the next sign-in, opens it again.
 */
let resumeChecked = false
onSessionEnd(() => {
  resumeChecked = false
})

const DOCKED: Record<PinPath, ComponentType> = {
  '/gate': lazy(() => import('../features/trust/Gate.js').then((m) => ({ default: m.Gate }))),
  '/wall': lazy(() => import('../features/wall/Wall.js').then((m) => ({ default: m.Wall }))),
  '/inbox': lazy(() => import('../features/chat/Inbox.js').then((m) => ({ default: m.Inbox }))),
}

/**
 * The shell (FR-MAP-15, decision 44): the default view is always mounted; every other signed-in screen
 * renders inside a popup over it. Closing the popup, by the X or by tapping outside, returns to `/map`.
 */
export function Shell() {
  const nav = useNavigate()
  // The page that's on screen, not the one being loaded: a popup stays until its page is done, so a
  // page transition (app/motion.ts) can see it go.
  const pathname = useRouterState({ select: (s) => (s.resolvedLocation ?? s.location).pathname })
  const { me, loaded, load } = useSession()
  // Learn More and the Safety Center are public (decision 62, WF-SAF-03): signed in, each is a popup
  // over the map like any other screen; signed out, there's no map, so it's the page itself.
  const publicPage = pathname === '/learn' || pathname.startsWith('/learn/') || pathname === '/safety'
  useEffect(() => {
    if (publicPage && !loaded) void load()
  }, [publicPage, loaded, load])
  const pinned = usePinned()
  // The page being opened, not the one still on screen: arriving from the front door, that's `/`.
  const opening = useRouterState({ select: (s) => s.location.pathname })
  useEffect(() => {
    if (!loaded || !me || resumeChecked) return
    resumeChecked = true
    if (opening !== '/map' && opening !== '/grid') return
    const to = openingRoute(me)
    // A Gate pinned beside the map is open already.
    if (to && !(to.to === '/gate' && pinned.some((p) => p.path === '/gate'))) nav({ ...to, replace: true })
  }, [loaded, me, opening, pinned, nav])
  const [trouble, setTrouble] = useState(0)
  // lib/diag.ts says something went wrong with this window event (TROUBLE there).
  useEffect(() => {
    const on = () => setTrouble((last) => (Date.now() - last < TROUBLE_QUIET_MS ? last : Date.now()))
    window.addEventListener('fm-trouble', on)
    return () => window.removeEventListener('fm-trouble', on)
  }, [])
  // One chat subscription for the whole signed-in app: the map, the inbox and a thread used to add one
  // each, so every message did its work three times (client review #5).
  useChatSocket()
  const qc = useQueryClient()
  const [flashing, setFlashing] = useState(false)
  const signedIn = !!me
  useEffect(() => {
    if (!signedIn) return
    return socket.on((f) => {
      if (f.t !== 'activity.new' && f.t !== 'message.new' && f.t !== 'message.reaction') return
      // Only while you're in the app; away, the push says it.
      if (document.visibilityState !== 'visible') return
      // The list as it was before this notice, taken now, before anything refetches it.
      const known = qc.getQueryData<ActivityReply>(['activity'])?.items.map((i) => i.id) ?? null
      setFlashing(true)
      void import('./Flashes.js').then((m) => m.hear(f, known, qc)).catch(() => {})
    })
  }, [signedIn, qc])
  // A pinned tab's own route shows the docked copy, not a second one in a popup. A thread opens under
  // the Inbox's tabs (bugs.md), so with the Inbox pinned it opens in the docked copy.
  const docked = pinned.some(
    (p) => pathname === p.path || (p.path === '/inbox' && /^\/inbox\/[^/]+$/.test(pathname)),
  )
  const open = pathname !== '/map' && pathname !== '/grid' && !docked
  // The map chrome on the docked side moves over (map.css).
  useEffect(() => {
    const root = document.documentElement
    for (const side of ['left', 'right'] as const) {
      const key = side === 'left' ? 'pinnedLeft' : 'pinnedRight'
      if (pinned.some((p) => p.side === side)) root.dataset[key] = ''
      else delete root.dataset[key]
    }
  }, [pinned])
  if (publicPage && !me)
    return loaded ? (
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    ) : null
  // WF-TRU-14: a removed account can reach the appeal and nothing else.
  if (loaded && me?.state === 'removed')
    return (
      <Suspense fallback={null}>
        <Appeal />
      </Suspense>
    )
  // Decision 71: once vouched for, an email is required; Anonymous accounts can go without.
  if (loaded && me && me.state !== 'anonymous' && me.state !== 'deleted' && !me.email)
    return (
      <Suspense fallback={null}>
        <EmailGate />
      </Suspense>
    )
  // Decision 75: the rules changed since this member accepted them; they accept the new ones first.
  if (loaded && me && me.rulesVersion !== null && me.rulesVersion < me.rulesCurrent)
    return (
      <Suspense fallback={null}>
        <RulesUpdate />
      </Suspense>
    )
  if (loaded && me && (me.termsVersion < me.termsCurrent || me.privacyVersion < me.privacyCurrent))
    return (
      <Suspense fallback={null}>
        <LegalUpdate />
      </Suspense>
    )
  return (
    <>
      <Suspense fallback={<div style={{ height: '100dvh', background: '#000' }} />}>
        <MapScreen />
      </Suspense>
      {pinned.map((p) => (
        <PinnedPanel key={p.path} path={p.path} side={p.side} />
      ))}
      {open && (
        <Popup onClose={() => nav({ to: home() })} wide={publicPage}>
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </Popup>
      )}
      {flashing && (
        <Suspense fallback={null}>
          <Flashes />
        </Suspense>
      )}
      {trouble > 0 && (
        <Suspense fallback={null}>
          <TroubleNote at={trouble} />
        </Suspense>
      )}
      {/* Your open meets, and the events you're on your way to (decision 193), keep sharing on any
          screen while the app is open (FR-MET-09). */}
      {me?.state === 'trusted' && (
        <Suspense fallback={null}>
          <MeetDuties />
          <EventMeetDuties />
          <NowBar />
        </Suspense>
      )}
    </>
  )
}

function PinnedPanel({ path, side }: { path: PinPath; side: 'left' | 'right' }) {
  const Docked = DOCKED[path]
  return (
    <PinnedContext.Provider value={path}>
      <section className={`fm-popup__panel fm-pinned fm-pinned--${side}`} aria-label="Pinned">
        <Suspense fallback={null}>
          <Docked />
        </Suspense>
      </section>
    </PinnedContext.Provider>
  )
}
